/**
 * Goonghap (Compatibility) Calculations — English output
 */
import type { GoonghapScores, GoonghapAdvice } from './goonghapCalculations';

const ADVICE_TABLE_EN: Record<string, {
  high: { title: string; advice: string; tip?: string };
  mid: { title: string; advice: string; tip?: string };
  low: { title: string; advice: string; tip?: string };
}> = {
  personality: {
    high: { title: 'A Match Made in Heaven', advice: 'Your core personalities click naturally. Being together feels effortless and comfortable.', tip: "That unexplainable ease from the very start? It's real 😊" },
    mid:  { title: 'A Unique Pair', advice: 'You share some similarities and some differences. Embracing those differences makes this relationship more interesting.' },
    low:  { title: 'Complete Opposites', advice: "Your fundamental personalities are quite different.", tip: "Reframe it: 'We clash because we're different' → 'We have so much to learn from each other.' Opposites often become the strongest teams." },
  },
  communication: {
    high: { title: 'On the Same Wavelength', advice: 'Even when you argue, communication flows. Most conflicts can be resolved through open dialogue.' },
    mid:  { title: 'Different Frequencies', advice: "Sometimes it feels like you're tuned to different channels.", tip: "When emotions run high, the one who says 'Let's take a break and talk later' wins 🤫" },
    low:  { title: 'You Need a Translator', advice: "Your communication styles diverge significantly during conflict.", tip: "Absolutely no bringing up the past in arguments. This combination is hard to recover from once a line is crossed. 2 dates/week + personal space is strongly recommended!" },
  },
  wealth: {
    high: { title: 'Golden Financial Synergy', advice: 'Your finances flow better together. Setting shared financial goals is a great idea.', tip: 'Opening a joint savings account is a great first step 💸' },
    mid:  { title: 'Steady Financial Flow', advice: 'No major synergy or conflict in finances — a stable, unremarkable combination.' },
    low:  { title: 'Manage Finances Separately', advice: "There's a real risk of financial loss if you invest or start a business together.", tip: 'Keep finances strictly separate and only create a shared account for joint expenses. Never invest in stocks or crypto together 😅' },
  },
  affection: {
    high: { title: 'You Complete Each Other', advice: "You're filling exactly what the other needs. Being together brings genuine psychological stability and strength.", tip: "Person A's missing energy is precisely what Person B provides 🧩" },
    mid:  { title: 'A Balanced Relationship', advice: "The elemental balance is good. You naturally complement each other and attraction comes easily." },
    low:  { title: 'Side by Side, Not Filling In', advice: "You share similar energies, so you're on parallel journeys rather than filling each other's gaps.", tip: "Don't force a fit. Approach it as growing together — that's actually the strength 💪" },
  },
  timing: {
    high: { title: 'Perfect Timing Right Now', advice: "Your fortune cycles are both pointing in the same direction this year. The timing to be together is genuinely excellent." },
    mid:  { title: 'Average Timing', advice: "The current timing isn't bad for you both — steady and unremarkable." },
    low:  { title: 'Time Heals All', advice: "Your fortune cycles are slightly out of sync right now.", tip: "Rather than major decisions (moving in, marriage, joint ventures) right now, take time to get to know each other slowly ⏳" },
  },
};

export function generateAdviceEn(scores: GoonghapScores): GoonghapAdvice[] {
  const categoryMap: { key: keyof GoonghapScores; label: string; icon: string }[] = [
    { key: 'personality',   label: 'Personality',    icon: '🔥' },
    { key: 'communication', label: 'Communication',  icon: '💬' },
    { key: 'wealth',        label: 'Wealth',          icon: '💰' },
    { key: 'affection',     label: 'Affection',       icon: '💖' },
    { key: 'timing',        label: 'Timing',          icon: '⏳' },
  ];
  return categoryMap.map(({ key, label, icon }) => {
    const score = scores[key] as number;
    const table = ADVICE_TABLE_EN[key];
    const entry = score >= 68 ? table.high : score >= 40 ? table.mid : table.low;
    return { category: label, icon, score, title: entry.title, advice: entry.advice, tip: entry.tip };
  });
}

export function getSummaryMessageEn(
  total: number,
  nameA: string,
  nameB: string
): { headline: string; sub: string; emoji: string; isFakPok: boolean } {
  const a = nameA || 'Person A';
  const b = nameB || 'Person B';
  if (total >= 80) return {
    headline: `${a} & ${b}'s BarunSaju Compatibility Score: ${total}!`,
    sub: 'You fit each other like perfectly matched puzzle pieces 🧩',
    emoji: '💝', isFakPok: false,
  };
  if (total >= 65) return {
    headline: `${a} & ${b}'s BarunSaju Compatibility Score: ${total}!`,
    sub: 'A solid foundation. Add mutual understanding and you\'re golden ✨',
    emoji: '💫', isFakPok: false,
  };
  if (total >= 50) return {
    headline: `${a} & ${b}'s BarunSaju Compatibility Score: ${total}!`,
    sub: 'Average compatibility. This relationship can thrive with sincere effort 🌱',
    emoji: '🌿', isFakPok: false,
  };
  if (total >= 40) return {
    headline: `This relationship needs courage. ${total} points`,
    sub: "Not easy — but understanding this compatibility can make you a formidable pair 💪",
    emoji: '⚡', isFakPok: true,
  };
  return {
    headline: `Love alone won't be enough for this one. Difficulty: MAX. ${total} points`,
    sub: "You're not matching puzzle pieces. Forcing it will just wear down your edges 💔",
    emoji: '🚨', isFakPok: true,
  };
}

export const SURVIVOR_TIPS_EN = [
  'Respect each other\'s personal space to the absolute limit. Spending every moment together will always lead to conflict. (2 dates per week is recommended)',
  'Never bring up past issues during arguments. Once a line is crossed in this compatibility, recovery is genuinely difficult.',
  'Major decisions (moving in together, marriage, joint ventures) should wait at least 2 years into the relationship.',
  "Don't try to 'fix' each other. Simply accept that you are different people with different wiring.",
];